import { DecimalPipe, NgClass, NgIf } from '@angular/common';
import { Component, inject, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormsModule, NgModel, ValidationErrors } from '@angular/forms';
import { MatIconButton, MatMiniFabButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatCell, MatCellDef, MatColumnDef, MatFooterCell, MatFooterCellDef, MatFooterRow, MatFooterRowDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable, MatTableDataSource } from '@angular/material/table';
import { SpritzWaldarbeitenItem } from '@gsm/entities';
import { WINDOW } from 'ngx-window-token';
import { filterEmptyProps } from '../../helpers/filter-props.helper';
import { AlphanumValidator } from '../../validators/alphanum.validator';
import { CustomDynamicFormControlComponent } from '../custom-dynamic-form-control.component';
import { HaftpflichtSpritzWaldarbeitenAufstellungModel } from './haftpflicht-spritz-waldarbeiten-aufstellung.model';

// NOTE:
// The base class CustomDynamicFormControlComponent already includes all inputs and outputs needed, as well
// as additional convenience methods onDisabledChange() and onValueChange, for when model.disabled and model.value change,
// respectively.

@Component({
  selector:    'gsm-haftpflicht-spritz-waldarbeiten-aufstellung',
  templateUrl: 'haftpflicht-spritz-waldarbeiten-aufstellung.component.html',
  styleUrls:   ['haftpflicht-spritz-waldarbeiten-aufstellung.component.scss'],
  standalone:  true,
  imports:     [
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatInput,
    FormsModule,
    AlphanumValidator,
    MatFooterCellDef,
    MatFooterCell,
    NgClass,
    NgIf,
    MatIconButton,
    MatIcon,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatFooterRowDef,
    MatFooterRow,
    MatMiniFabButton,
    DecimalPipe
  ]
})
export class HaftpflichtSpritzWaldarbeitenAufstellungComponent
  extends CustomDynamicFormControlComponent<HaftpflichtSpritzWaldarbeitenAufstellungModel>
  implements OnInit {

  private window = inject<Window>(WINDOW);

  numberPattern = '^(0|[1-9][0-9]*)$';
  displayedColumns = [
    'art',
    'bezeichnung',
    'anzahl',
    'stueckpreis',
    'betrag',
    'delete'
  ];
  itemList = new MatTableDataSource<SpritzWaldarbeitenItem>();

  private _requiredFields = [
    'art',
    'anzahl',
    'stueckpreis'
  ];

  @ViewChildren('art') artModels: QueryList<NgModel>;

  constructor() {
    super();
  }

  ngOnInit() {
    super.ngOnInit();
  }

  onModelChange() {
    if (!this.model.value || !this.model.value.positionen || !this.model.value.positionen.length) {
      this.itemList.data = [];
      this.addItem();
    } else {
      this.itemList.data = this.model.value.positionen;
    }
    this.window.setTimeout(() => {
      this._emitChange();
    }, 0);
  }

  get totalSum(): number {
    return this.itemList.data.reduce((total, item) => {
      const betrag = item.betrag || 0;
      return total + betrag;
    }, 0);
  }

  addItem() {
    const newElItem: SpritzWaldarbeitenItem = {
      art:         null,
      bezeichnung: null,
      anzahl:      null,
      stueckpreis: null,
      betrag:      null
    };
    const data = this.itemList.data;
    data.push(newElItem);
    this.itemList.data = data;
    this.onFormValueChange();
  }

  deleteItem(index: number) {
    if (this.itemList.data.length > 1) {
      const data = this.itemList.data;
      data.splice(index, 1);
      this.itemList.data = data;
      this.onFormValueChange();
    } else if (index === 0) {
      this.resetItem(this.itemList.data[0]);
    }
  }

  onInputBlur(item?: SpritzWaldarbeitenItem) {
    if (item) {
      this._updateRowBetrag(item);
    }

    this.onFormValueChange();
  }

  private _updateRowBetrag(item: SpritzWaldarbeitenItem) {
    const anzahl = item.anzahl || 0;
    const stueckpreis = item.stueckpreis || 0;
    item.betrag = anzahl * stueckpreis;
  }

  onFormValueChange() {
    this.model.value = {
      positionen: this.itemList.data.map(item => filterEmptyProps(item)),
      summe:      this.totalSum
    };
    this._markRequiredFieldsTouched();
    this._emitChange();
  }

  private _markRequiredFieldsTouched() {
    if (this.artModels) {
      this.artModels.forEach(mod => mod.control.markAsTouched());
    }
  }

  resetItem(item: SpritzWaldarbeitenItem) {
    item.art = null;
    item.bezeichnung = null;
    item.anzahl = null;
    item.stueckpreis = null;
    item.betrag = null;
    this.onFormValueChange();
  }

  private _emitChange() {
    this.group.get(this.model.id)
      .setErrors(this._getErrors());
    this.change.emit(this.model.value);
  }

  private _getErrors(): ValidationErrors {
    const invalid = this.totalSum <= 0 || this.itemList.data.some(item => item && this._requiredFields.some(field => !item[field]));
    return invalid ? {spritzWaldarbeitenAufstellung: true} : null;
  }
}
